import type { DateRangePickerProps } from '@risk-smart/themed-cloudscape-components/date-range-picker';
import { defaultDashboardFilter } from 'src/context/defaultDashboardFilter';
import { processWidgets } from 'src/context/processWidgets';
import { create } from 'zustand';

import type { DashboardView } from './dashboard-view-toggle/types';
import type { StoredWidgetPlacement } from './types';

export interface DashboardFilter {
  departments: string[];
  tags: string[];
  dateRange: DateRangePickerProps.Value | null;
}

export interface MyItemsFilter {
  owner: boolean;
  contributor: boolean;
  groupOwner: boolean;
  groupContributor: boolean;
  inheritedOwner: boolean;
  inheritedContributor: boolean;
  inheritedGroupOwner: boolean;
  inheritedGroupContributor: boolean;
}

export type State = {
  id?: string;
  filters: DashboardFilter;
  widgets: StoredWidgetPlacement[];
  myItemsFilters: MyItemsFilter;
  myItemsWidgets: StoredWidgetPlacement[];
  selectedDashboard: DashboardView;
};

export type OverallDashboardState = Omit<
  State,
  'myItemsFilters' | 'myItemsWidgets' | 'selectedDashboard'
>;

export type Actions = {
  setFilters: (filters: DashboardFilter) => void;
  setWidgets: (widgets: StoredWidgetPlacement[]) => void;
  setMyItemsFilters: (filters: MyItemsFilter) => void;
  setMyItemsWidgets: (widgets: StoredWidgetPlacement[]) => void;
  setId: (id: string) => void;
  setDashboardPreferences: (
    dashboardPreferences: OverallDashboardState,
    myItemsWidgets?: StoredWidgetPlacement[],
    myItemsFilters?: MyItemsFilter
  ) => void;
  setSelectedDashboard: (selectedDashboard: DashboardView) => void;
};

export const defaultMyItemsFilter: MyItemsFilter = {
  owner: true,
  contributor: false,
  groupOwner: false,
  groupContributor: false,
  inheritedOwner: false,
  inheritedContributor: false,
  inheritedGroupOwner: false,
  inheritedGroupContributor: false,
};

export const initialDashboardState: State = {
  id: undefined,
  filters: defaultDashboardFilter,
  widgets: [],
  myItemsFilters: defaultMyItemsFilter,
  myItemsWidgets: [],
  selectedDashboard: 'my-items',
};

/**
 * Actions live under a stable `actions` key so `useDashboardActions()` selects a
 * value that never changes identity and therefore never re-renders its consumer.
 *
 * `processWidgets` runs on WRITE only — in `setWidgets`, `setMyItemsWidgets` and
 * `setDashboardPreferences`. That establishes the invariant *widget arrays in
 * the store are always processed*, which is what lets read paths (and the
 * per-widget selector in `useDashboardWidgetSettings`) return a stable identity.
 * Do not add a `processWidgets` call to a read path.
 */
export const dashboardStore = create<State & { actions: Actions }>()((set) => ({
  ...initialDashboardState,
  actions: {
    setFilters: (filters) => set({ filters }),
    setWidgets: (widgets) => set({ widgets: processWidgets(widgets) }),
    setMyItemsFilters: (myItemsFilters) => set({ myItemsFilters }),
    setMyItemsWidgets: (myItemsWidgets) =>
      set({ myItemsWidgets: processWidgets(myItemsWidgets) }),
    setId: (id) => set({ id }),
    // The my-items keys are set explicitly rather than left to the
    // `dashboardPreferences` spread: callers pass an object that can carry
    // stray `myItemsWidgets` / `myItemsFilters` keys (TS excess-property checks
    // do not fire on a variable), and an omitted argument must LEAVE the
    // existing value alone rather than clear it. `id` is deliberately not
    // defaulted — a payload with no `id` key leaves the store's id untouched,
    // which is what hydration relies on.
    setDashboardPreferences: (
      dashboardPreferences,
      myItemsWidgets,
      myItemsFilters
    ) =>
      set((state) => ({
        ...dashboardPreferences,
        widgets: processWidgets(dashboardPreferences.widgets),
        myItemsWidgets: myItemsWidgets
          ? processWidgets(myItemsWidgets)
          : state.myItemsWidgets,
        myItemsFilters: myItemsFilters ?? state.myItemsFilters,
      })),
    setSelectedDashboard: (selectedDashboard) => set({ selectedDashboard }),
  },
}));

export const useDashboardFilters = () => dashboardStore((s) => s.filters);
export const useDashboardWidgets = () => dashboardStore((s) => s.widgets);
export const useMyItemsFilters = () => dashboardStore((s) => s.myItemsFilters);
export const useMyItemsWidgets = () => dashboardStore((s) => s.myItemsWidgets);
export const useSelectedDashboard = () =>
  dashboardStore((s) => s.selectedDashboard);
export const useDashboardId = () => dashboardStore((s) => s.id);
export const useDashboardActions = () => dashboardStore((s) => s.actions);
